'use client';
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import {
  resetGuard,
  setCriticalData,
  setGuardActive,
  setGuardMessage,
  setUnloadCallback,
} from '@/lib/slices/route-guard-slice';
import { setCurrentQuizQuestions, setQuizJustSubmitted } from '@/lib/slices/quiz-slice';

import QuizQuestion from './QuizQuestion';
import QuizQuestionsList from './QuizQuestionsList';
import QuizReview from './QuizReview';
import ActivityNavButtons from '@/(portal)/student/lesson/screens/components/ActivityNavButtons';
import Button from '@/common/components/ui/Button';

import Request from '@/services/util/request-service';
import ProgressUtilityService from '@/services/course/progress-utility-service';
import UserProgressStorageService from '@/services/storage/user-progress-storage-service';
import ActiveLessonCacheService from '@/services/storage/active-lesson-cache-service';
import ActivityUtilityService from '@/services/course/lesson/activity/activity-utility-service';
import QuizUtilityService from '@/services/course/lesson/quiz-utility-service';

import {
  ActivityData,
  CourseId,
  QuizAttempt,
  QuizCompletionData,
  SubunitId,
  UnitId,
  BlockingContent,
} from 'course-types';
import { ActiveQuizCookie, QuizQuestionData } from 'quiz-types';
import { ArrowBackIcon, ArrowForwardIcon } from '@/assets/ui/navigation';
import ActiveGroupCacheService from '@/services/storage/active-group-cache-service';
import LockedContent from '@/common/components/LockedContent';

interface Props {
  quizId: string;
  subunitId: SubunitId | undefined;
  unitId: UnitId | undefined;
  courseId: CourseId;
  onBack?: () => void;
}

const WORD_COUNT_LIMIT = 300;
const CHARACTER_LIMIT = 2000;

const Quiz = ({ quizId, subunitId, unitId, courseId, onBack }: Props) => {
  const store = useAppStore();
  const dispatch = useAppDispatch();

  const course = useAppSelector(() => store.getState().course.activeCourseData);
  const groupId = ActiveGroupCacheService.getCurrentGroupCookie();
  const activity: ActivityData = useAppSelector(() => store.getState().activity);

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [questionSet, setQuestionSet] = useState<Array<QuizQuestionData>>([]);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, number | string>>({});
  const [submittedAnswers, setSubmittedAnswers] = useState<Record<number, number | string>>({});
  const [attemptStartDate, setAttemptStartDate] = useState<Date | string>();
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
  const [submitData, setSubmitData] = useState<any>(null);
  const [locked, setLocked] = useState<boolean>(false);
  const [blockingContent, setBlockingContent] = useState<BlockingContent>({} as BlockingContent);

  useEffect(() => {
    const prepareQuizActivity = async () => {
      const quizData = await QuizUtilityService.retrieveQuiz(quizId, !onBack);
      if (quizData?.error && quizData.blockingContent) {
        setLocked(true);
        setBlockingContent(quizData.blockingContent as BlockingContent);
        return;
      }
      setLocked(false);
      setBlockingContent({} as BlockingContent);
      const { quiz, attempt } = quizData;
      setQuestionSet(quiz);
      // Store quiz questions in Redux for flashcard generation
      dispatch(setCurrentQuizQuestions(quiz));
      // Reset submission flag when quiz loads
      dispatch(setQuizJustSubmitted(false));

      const activeQuizCookie: ActiveQuizCookie | undefined =
        ActiveLessonCacheService.getActiveQuizCookie();
      if (!activeQuizCookie) {
        if (attempt) {
          // Previously started, newly re-opened
          handlePreviouslyStartedQuiz(attempt);
        } else {
          // New quiz attempt
          await handleNewQuizAttempt();
        }
      } else {
        // Reloaded page. Set cookie to state, retrieve questions
        handleReloadedQuizCookie(activeQuizCookie);
      }
    };

    prepareQuizActivity().catch(console.error);
  }, [quizId]);

  const handlePreviouslyStartedQuiz = (attempt: Partial<QuizAttempt>) => {
    const cookie = QuizUtilityService.buildQuizCookieFromAttempt(attempt);
    ActiveLessonCacheService.setActiveQuizCookie(cookie);
    setSelectedAnswers(cookie.data ?? {});
    setAttemptStartDate(cookie.start_time ?? 'Error');
  };

  const handleNewQuizAttempt = async () => {
    const newStartTime = new Date();
    setSelectedAnswers({});

    if (!onBack) {
      const newAttempt = await (
        await Request.post(`quiz/attempt/create`, {
          quizId: quizId,
          startDate: newStartTime,
        })
      )?.json();

      if (!newAttempt || newAttempt.error) {
        toast.error('Something went wrong on setting up your quiz.');
        return;
      }
      ActiveLessonCacheService.setActiveQuizCookie({ start_time: newAttempt.start_date, data: {} });
      setAttemptStartDate(newAttempt.start_date);
    } else {
      ActiveLessonCacheService.setActiveQuizCookie({ start_time: newStartTime, data: {} });
      setAttemptStartDate(newStartTime);
    }
  };

  const handleReloadedQuizCookie = (activeQuizCookie: ActiveQuizCookie) => {
    setSelectedAnswers(activeQuizCookie.data ?? {});
    setAttemptStartDate(activeQuizCookie.start_time ?? 'Error');
  };

  // Update backup data used for premature page navigation
  useEffect(() => {
    if (selectedAnswers != undefined && Object.keys(selectedAnswers).length > 0) {
      dispatch(
        setCriticalData({
          backupAnswers: { ...(selectedAnswers ?? {}) },
          backupStart: attemptStartDate,
          questionSet: [...(questionSet ?? [])],
        }),
      );
    }
  }, [selectedAnswers, attemptStartDate, questionSet]);

  // Updates progress on quiz open
  useEffect(() => {
    if (
      quizId &&
      ((subunitId && subunitId != ('' as SubunitId)) ||
        (unitId && unitId != ('' as UnitId)) ||
        unitId) &&
      courseId
    ) {
      onOpen();
    }
  }, [quizId, unitId, subunitId, courseId]);

  // Set Route Guard for early page navigation mid-quiz
  useEffect(() => {
    if (!submitSuccess && !onBack) {
      dispatch(setGuardActive(true));
      dispatch(
        setGuardMessage(
          'Leaving this page may not properly save your current attempt progress. Are you sure you want to leave?',
        ),
      );
      dispatch(setUnloadCallback('IncompleteQuizNav'));
    }
    return () => {
      dispatch(resetGuard());
    };
  }, [submitSuccess, onBack]);

  const onOpen = async () => {
    // TODO:: re-pull progress if cookie not set / cleared
    //        Edge case where current set method may overwrite database progress

    const { progressCookie, activityData } = ActivityUtilityService.retrieveActivityProgressLocal(
      courseId,
      unitId,
      subunitId,
    );

    // Define progress if undefined
    if (!activityData.quiz[quizId]) {
      activityData.quiz[quizId] = { attempts: 0 };
    }

    // Set openDate in DB if undefined
    if (!activityData.quiz[quizId]?.openDate) {
      activityData.quiz[quizId].openDate = new Date();

      await ProgressUtilityService.updateUserCourseProgressDatabase(
        courseId as CourseId,
        activityData,
        unitId as UnitId,
        subunitId as SubunitId,
      );
    }

    // Set progress cookie with new data
    ActivityUtilityService.updateActivityProgressLocal(
      progressCookie,
      activityData,
      courseId,
      unitId,
      subunitId,
    );
  };

  const onAnswerClick = (questionIndex: number, answerIndex: number | string): void => {
    const newSelectedAnswers: Record<number, number | string> = { ...selectedAnswers };
    newSelectedAnswers[questionIndex] = answerIndex;

    setSelectedAnswers(newSelectedAnswers);
    ActiveLessonCacheService.setActiveQuizCookie({
      ...ActiveLessonCacheService.getActiveQuizCookie(),
      data: newSelectedAnswers,
    });
  };

  const onAnswerInput = (questionIndex: number, value: string): void => {
    if (value.split(' ').length > WORD_COUNT_LIMIT || value.length > CHARACTER_LIMIT) {
      toast.error('Past word limit.');
      return;
    }
    const newSelectedAnswers: Record<number, number | string> = { ...selectedAnswers };
    newSelectedAnswers[questionIndex] = value;

    setSelectedAnswers(newSelectedAnswers);
    ActiveLessonCacheService.setActiveQuizCookie({
      ...ActiveLessonCacheService.getActiveQuizCookie(),
      data: newSelectedAnswers,
    });
    return;
  };

  const handlePreviousQuestion = () => {
    setSubmittedAnswers(selectedAnswers);
    setCurrentQuestionIndex((currentIndex) => currentIndex - 1);
  };

  const handleNextQuestion = () => {
    setSubmittedAnswers(selectedAnswers);
    setCurrentQuestionIndex((currentIndex) => currentIndex + 1);
  };

  const getCurrentQuestionData = (): QuizQuestionData => {
    return questionSet[currentQuestionIndex];
  };

  const swapQuestion = (index: number) => {
    setCurrentQuestionIndex(index);
  };

  const isSubmitDisabled = (): boolean => {
    if (Object.keys(selectedAnswers).length < questionSet.length) {
      return true;
    }
    for (const q in selectedAnswers) {
      if (typeof selectedAnswers[q] === 'string' && selectedAnswers[q] == '') {
        return true;
      }
    }
    return false;
  };

  const handleSubmit = async () => {
    if (Object.keys(selectedAnswers).length < questionSet.length) {
      toast.error('Please answer all questions before submitting.');
      return;
    }

    setSubmittedAnswers(selectedAnswers);
    const res = await QuizUtilityService.submitQuiz(
      quizId,
      selectedAnswers,
      course.id,
      attemptStartDate ?? 'Error',
      groupId ?? '',
    );

    // success
    if (res && 'submission' in res) {
      // Update User Progress locally (already updated in database)
      const lessonStateCookie = ActiveLessonCacheService.getLessonStateCookie();
      if (!lessonStateCookie) {
        toast.error('Failed to update progress locally. Try logging back in.');
      } else {
        const progressCookie = ProgressUtilityService.validateCourseDataExists(
          UserProgressStorageService.getUserCourseProgressCookie() ?? {},
          courseId,
          unitId,
          subunitId,
        );
        let quizData: QuizCompletionData | undefined = subunitId
          ? progressCookie[courseId].completion_data.subunits[subunitId].quiz[quizId]
          : progressCookie[courseId].completion_data.units[unitId as UnitId].quiz[quizId];
        if (!quizData) {
          quizData = {
            initialSubmissionDate: res.submission as Date,
            attempts: 0,
          };
        }
        if (!quizData.initialSubmissionDate) {
          quizData.initialSubmissionDate = res.submission as Date;
        }
        quizData.attempts = (quizData.attempts ?? 0) + 1;
        if (subunitId) {
          progressCookie[courseId].completion_data.subunits[subunitId].quiz[quizId] = {
            ...quizData,
          };
        } else {
          progressCookie[courseId].completion_data.units[unitId as UnitId].quiz[quizId] = {
            ...quizData,
          };
        }

        UserProgressStorageService.setUserCourseProgressCookie(progressCookie);
      }

      toast.success('Successfully submitted quiz!');
      setSubmitSuccess(true);
      setSubmitData(res);
      // Set flag to show flashcard button on results screen
      dispatch(setQuizJustSubmitted(true));

      // remove from local storage
      ActiveLessonCacheService.resetActiveQuizCookie();
      // TODO: set completion data
    }
  };

  if (locked) {
    return (
      <LockedContent
        blockingContent={blockingContent}
        courseId={courseId}
        courseName={course?.name}
      />
    );
  }

  if (questionSet.length === 0) {
    return <div>Loading...</div>;
  }

  if (submitSuccess) {
    return (
      <QuizReview
        submitData={submitData}
        questionSet={questionSet}
        answers={selectedAnswers}
        quizName={activity?.name}
      />
    );
  }

  return (
    <div className='flex h-full mx-auto w-full lg:max-w-[1200px] md:max-w-[720px] gap-6'>
      <div className='flex flex-col flex-grow'>
        <div className='flex flex-grow flex-col bg-surface_gray_50 border border-border_gray_200 drop-shadow-md p-6 rounded-xl justify-between gap-6'>
          <QuizQuestion
            questionData={getCurrentQuestionData()}
            onAnswerClick={onAnswerClick}
            onAnswerInput={onAnswerInput}
            currentAnswer={selectedAnswers[currentQuestionIndex] ?? undefined}
          />
          <div className='flex flex-row'>
            <Button
              text={true}
              className={`flex justify-center w-fit mr-auto hover:text-primary ${currentQuestionIndex != 0 ? '' : 'hidden'}`}
              onClick={handlePreviousQuestion}
            >
              <ArrowBackIcon className='w-8' />
              Previous
            </Button>
            <Button
              text={true}
              className={`flex justify-center w-fit ml-auto hover:text-primary ${currentQuestionIndex != questionSet.length - 1 ? '' : 'hidden'}`}
              onClick={handleNextQuestion}
            >
              Next
              <ArrowForwardIcon className='w-8' />
            </Button>
          </div>
        </div>
        {onBack ? (
          <>
            {/* Spacer to keep bottom of quiz above the fixed nav buttons */}
            <div className='h-[80px] flex-none' />
            <ActivityNavButtons onBack={onBack} />
          </>
        ) : (
          <Button
            className='w-full mt-4 lg:hidden flex'
            onClick={handleSubmit}
            disabled={isSubmitDisabled()}
          >
            Submit Quiz
          </Button>
        )}
      </div>
      <QuizQuestionsList
        questionIndex={currentQuestionIndex}
        questionSet={questionSet}
        submittedAnswers={selectedAnswers}
        swapQuestion={swapQuestion}
        isSubmitDisabled={isSubmitDisabled}
        handleSubmit={handleSubmit}
        onBack={onBack}
      />
    </div>
  );
};

export default Quiz;
